/**
 * Client-side authenticated fetch wrapper for the SPA architecture.
 *
 * - Stores the access token in-memory only (never localStorage) for XSS protection.
 * - Attaches `Authorization: Bearer <token>` on every request.
 * - On 401, transparently calls POST /auth/refresh (cookie sent automatically).
 * - Queues concurrent requests during a token refresh to avoid race conditions.
 */

const BASE_URL = (import.meta.env.VITE_BASE_BACKEND_URL || 'http://localhost:8080') as string

// ---------------------------------------------------------------------------
// In-memory access token store
// ---------------------------------------------------------------------------

let accessToken: string | null = null

export function getAccessToken(): string | null {
  return accessToken
}

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function clearAccessToken(): void {
  accessToken = null
}

// ---------------------------------------------------------------------------
// Token refresh queue — prevents parallel refresh calls
// ---------------------------------------------------------------------------

let isRefreshing = false
let refreshPromise: Promise<string | null> | null = null

/**
 * Call POST /auth/refresh. The httpOnly refresh cookie is sent automatically
 * because the request goes to the same origin (or CORS with credentials).
 * Returns the new access token, or null if the refresh failed.
 */
async function refreshAccessToken(): Promise<string | null> {
  try {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include', // send refresh cookie
      headers: { 'Content-Type': 'application/json' },
    })

    if (!res.ok) return null

    const data = await res.json()
    const newToken: string | undefined = data?.data?.accessToken
    if (!newToken) return null

    setAccessToken(newToken)
    return newToken
  } catch {
    return null
  }
}

/**
 * Ensures only one refresh is in-flight at a time.
 * Concurrent callers wait on the same promise.
 */
async function refreshOnce(): Promise<string | null> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  isRefreshing = true
  refreshPromise = refreshAccessToken().finally(() => {
    isRefreshing = false
    refreshPromise = null
  })

  return refreshPromise
}

// ---------------------------------------------------------------------------
// authFetch — drop-in replacement for fetch with auth + auto-retry
// ---------------------------------------------------------------------------

export interface AuthFetchOptions extends RequestInit {
  /** If true, skip attaching the Authorization header (e.g., for login/signup). */
  skipAuth?: boolean
}

/**
 * Authenticated fetch wrapper.
 *
 * Usage:
 * ```ts
 * const res = await authFetch('/volunteers', { method: 'GET' })
 * const data = await res.json()
 * ```
 */
export async function authFetch(
  urlOrPath: string,
  options: AuthFetchOptions = {},
): Promise<Response> {
  const { skipAuth, ...fetchOptions } = options

  // Resolve full URL if a relative path was provided
  const url = urlOrPath.startsWith('http') ? urlOrPath : `${BASE_URL}${urlOrPath}`

  const headers: Record<string, string> = {
    ...(fetchOptions.headers as Record<string, string>),
  }

  // Attach Bearer token unless explicitly skipped
  if (!skipAuth && accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  // Default to JSON content type for non-FormData bodies
  if (fetchOptions.body && !(fetchOptions.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json'
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
    credentials: 'include', // always send cookies (for refresh endpoint)
  })

  // On 401, attempt a silent token refresh and retry once
  if (response.status === 401 && !skipAuth) {
    const newToken = await refreshOnce()
    if (newToken) {
      const retryHeaders: Record<string, string> = {
        ...(fetchOptions.headers as Record<string, string>),
        'Authorization': `Bearer ${newToken}`,
      }
      if (fetchOptions.body && !(fetchOptions.body instanceof FormData) && !retryHeaders['Content-Type']) {
        retryHeaders['Content-Type'] = 'application/json'
      }
      return fetch(url, {
        ...fetchOptions,
        headers: retryHeaders,
        credentials: 'include',
      })
    }
  }

  return response
}

// ---------------------------------------------------------------------------
// Helper: parse API response envelope
// ---------------------------------------------------------------------------

import { ApiResponse } from '@/shared/types/backend-response.type'

/**
 * Convenience wrapper that calls authFetch and parses the JSON response.
 * Returns a typed ApiResponse<T>.
 */
export async function authFetchJson<T>(
  urlOrPath: string,
  options: AuthFetchOptions = {},
): Promise<ApiResponse<T>> {
  try {
    const response = await authFetch(urlOrPath, options)

    if (!response.ok) {
      const errorText = await response.text()
      let errorMessage = errorText
      try {
        const errorJson = JSON.parse(errorText)
        errorMessage = errorJson.message || errorText
      } catch {
        // keep original text
      }
      if (!errorMessage || String(errorMessage).trim() === '') {
        errorMessage = `${response.status} ${response.statusText}`
      }
      return {
        success: false,
        message: errorMessage,
        status: response.status,
      } as ApiResponse<T>
    }

    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      return {
        success: false,
        message: `Expected JSON but got: ${response.status} ${response.statusText}`,
        status: response.status,
      } as ApiResponse<T>
    }

    return (await response.json()) as ApiResponse<T>
  } catch (error) {
    const errMessage = error instanceof Error ? error.message : String(error)
    return {
      success: false,
      message: `Network error: ${errMessage}`,
    } as ApiResponse<T>
  }
}
